import React, { useState, useEffect } from 'react';
import axios from './utils/axiosConfig';
import 'bootstrap/dist/css/bootstrap.min.css';

const EmpLineinfo = () => {
  const [employees, setEmployees] = useState([]);
  const [storeNames, setStoreNames] = useState([]); // 儲存所有 store_name 的列表
  const [selectedStore, setSelectedStore] = useState(''); // 儲存選中的 store_name
  const [page, setPage] = useState(1); // 初始頁碼為 1
  const [hasMore, setHasMore] = useState(true); // 是否還有更多資料
  const [imageErrors, setImageErrors] = useState({}); // 記錄圖片載入失敗的員工
  const token = localStorage.getItem('token');

  // 獲取所有 store_name 用於下拉選單
  const fetchStoreNames = async () => {
    try {
      const response = await axios.get('/stores', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      setStoreNames(response.data); // 假設 response.data 是 store_name 的數組
    } catch (error) {
      console.error('Error fetching store names:', error);
    }
  };

  // 獲取員工資料
  const fetchEmployees = async (currentPage, storeFilter) => {
    try {
      const response = await axios.get('/Allemployees', {
        headers: { 'Authorization': `Bearer ${token}` },
        params: { page: currentPage, store_name: storeFilter, filterWithLinePicture: 'true', allData: true }, // 只過濾有 LINE 照片的員工
      });
      console.log('API Response:', response.data);

      // 過濾有 e_line_picture_url 的員工
      const employeesWithPhotos = response.data.data || [];

      // 更新員工資料並合併新資料
      setEmployees(prevEmployees => currentPage === 1 ? employeesWithPhotos : [...prevEmployees, ...employeesWithPhotos]);
      setHasMore(response.data.hasMore); // 更新是否有更多資料的狀態
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  // 初始加載 store_name 列表
  useEffect(() => {
    fetchStoreNames();
  }, []);

  // 初始加載和每次頁碼或篩選條件變化時加載員工資料
  useEffect(() => {
    fetchEmployees(page, selectedStore);
  }, [page, selectedStore]);

  // 處理滾動事件以加載更多
  useEffect(() => {
    const handleScroll = () => {
      if (window.innerHeight + document.documentElement.scrollTop >= document.documentElement.offsetHeight - 50 && hasMore) {
        setPage(prevPage => prevPage + 1); // 加載下一頁
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasMore]);

  // 處理 store_name 下拉選單變更
  const handleStoreChange = (e) => {
    setSelectedStore(e.target.value);
    setPage(1); // 重置頁碼
    setEmployees([]); // 清空已加載的員工
    setImageErrors({}); // 清空圖片錯誤記錄
  };

  // 處理圖片載入錯誤
  const handleImageError = (employeeId) => {
    setImageErrors(prev => ({
      ...prev,
      [employeeId]: true
    }));
  };

  // 取得預設圖片
  const getDefaultAvatar = () => {
    return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iODAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjYwIiBoZWlnaHQ9IjgwIiBmaWxsPSIjZTBlMGUwIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZG9taW5hbnQtYmFzZWxpbmU9Im1pZGRsZSI+5peg54Wn54mHPC90ZXh0Pjwvc3ZnPg==';
  };

  // 檢查並驗證圖片 URL（優先級：LINE 圖片 > EDM_photo > e_photo）
  const getValidImageUrl = (employee) => {
    // 如果已經標記為錯誤，嘗試使用備用圖片
    if (imageErrors[employee.e_id]) {
      // 嘗試使用 EDM 照片作為備用
      if (employee.EDM_photo && employee.EDM_photo.trim() !== '') {
        return `/uploads/EDM_photo/${employee.EDM_photo}`;
      }
      // 再嘗試使用一般照片作為備用
      if (employee.e_photo && employee.e_photo.trim() !== '') {
        return `/uploads/employees/${employee.e_photo}`;
      }
      // 最後使用預設頭像
      return getDefaultAvatar();
    }

    // 優先使用 LINE 圖片
    const lineUrl = employee.e_line_picture_url;

    // 如果 LINE URL 存在且有效，使用 LINE 圖片
    if (lineUrl && lineUrl.trim() !== '' && lineUrl.match(/^https?:\/\/.+/i)) {
      return lineUrl;
    }

    // LINE 圖片無效時，標記為錯誤並嘗試備用圖片
    setImageErrors(prev => ({ ...prev, [employee.e_id]: true }));

    // 使用 EDM 照片作為備用
    if (employee.EDM_photo && employee.EDM_photo.trim() !== '') {
      return `/uploads/EDM_photo/${employee.EDM_photo}`;
    }

    // 使用一般照片作為備用
    if (employee.e_photo && employee.e_photo.trim() !== '') {
      return `/uploads/employees/${employee.e_photo}`;
    }

    // 最後使用預設頭像
    return getDefaultAvatar();
  };

  // 計算統計資訊
  const errorCount = Object.keys(imageErrors).length;
  const successCount = employees.length - errorCount;

  return (
    <div style={{ padding: '10px' }}>
      {/* 篩選的下拉選單 */}
      <div style={{ marginBottom: '15px', display: 'flex', justifyContent: 'center' }}>
        <label htmlFor="storeSelect" style={{ marginRight: '10px', fontWeight: 'bold' }}>選擇社別:</label>
        <select
          id="storeSelect"
          value={selectedStore}
          onChange={handleStoreChange}
          style={{
            width: '100%', 
            maxWidth: '300px', // 限制最大寬度
            padding: '8px',
            fontSize: '16px',
            borderRadius: '8px',
            border: '1px solid #ccc',
            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
            outline: 'none',
          }}
        >
          <option value="">全部社別</option>
          {storeNames.map((store) => (
            <option key={store.ss_id} value={store.ss_name}>{store.ss_name}</option>
          ))}
        </select>
      </div>

      {/* 統計資訊 */}
      {employees.length > 0 && (
        <div style={{
          marginBottom: '15px',
          padding: '10px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          textAlign: 'center',
          fontSize: '14px'
        }}>
          <span style={{ marginRight: '15px' }}>
            總共: <strong>{employees.length}</strong> 人
          </span>
          <span style={{ marginRight: '15px', color: '#28a745' }}>
            正常載入: <strong>{successCount}</strong>
          </span>
          <span style={{ color: '#dc3545' }}>
            載入失敗: <strong>{errorCount}</strong>
          </span>
          {errorCount > 0 && (
            <span style={{ marginLeft: '10px', fontSize: '12px', color: '#6c757d' }}>
              (LINE 圖片 URL 可能已過期)
            </span>
          )}
        </div>
      )}

      {/* 員工照片牆 */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
        gap: '10px', // 縮小卡片之間的間距
        textAlign: 'center'
      }}>
        {employees.map((employee, index) => (
          <div key={index} style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            lineHeight: '1.2',
            margin: 0,
            padding: 0,
          }}>
            <div style={{ position: 'relative' }}>
              <img
                src={getValidImageUrl(employee)}
                alt={employee.e_name}
                onError={() => handleImageError(employee.e_id)}
                loading="lazy"
                crossOrigin="anonymous"
                style={{
                  width: '60px',
                  height: '80px',
                  objectFit: 'cover',
                  borderRadius: '8px',
                  marginBottom: '4px',
                  border: imageErrors[employee.e_id] ? '2px solid #ff6b6b' : '1px solid #e0e0e0',
                  backgroundColor: '#f5f5f5'
                }}
              />
              {imageErrors[employee.e_id] && (
                <div style={{
                  position: 'absolute',
                  top: '-2px',
                  right: '-2px',
                  backgroundColor: '#ff6b6b',
                  color: 'white',
                  fontSize: '10px',
                  padding: '2px 5px',
                  borderRadius: '50%',
                  fontWeight: 'bold',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }}>
                  !
                </div>
              )}
            </div>
            <p style={{ margin: 0, fontWeight: 'bold' }}>{employee.store_name}</p> {/* 顯示社別 */}
            <p style={{ margin: 0, fontWeight: 'bold' }}>{employee.position_title}</p> {/* 顯示社職 */}
            <p style={{ margin: 0 }}>{employee.e_name}</p> {/* 顯示姓名 */}
          </div>
        ))}
      </div>
    </div>
  );
};

export default EmpLineinfo;
